import { useEffect, useMemo, useState } from 'react'
import { useRepos } from '../../state/repos'
import type {
  BookContent,
  GarmentContent,
  GarmentSection,
  PatternContent,
  ProjectContent,
} from '../../types/records'
import {
  computeWithInheritance,
  measurementsFromSize,
  type ComputeResult,
} from '../../geometry/computeGarment'
import { isDerivedCode } from '../../geometry/derived'
import { PiecePreview } from './PiecePreview'
import { printPiecesA0 } from './printPiecesA0'

const SECTION_LABELS: Record<GarmentSection, string> = {
  donna: 'Donna',
  uomo: 'Uomo',
  bambino: 'Bambino',
}

interface SavedVersion {
  id: string
  version: number
  createdAt: number
  content: PatternContent
}

interface Props {
  projectId: string
  onBack: () => void
  onDeleted: () => void
}

export function ProjectDetail({ projectId, onBack, onDeleted }: Props) {
  const { projects, garments, books, patterns } = useRepos()

  const [project, setProject] = useState<ProjectContent | null>(null)
  const [garment, setGarment] = useState<GarmentContent | null>(null)
  const [allGarments, setAllGarments] = useState<GarmentContent[]>([])
  const [book, setBook] = useState<BookContent | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const [ease, setEase] = useState<number>(4)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<ProjectContent | null>(null)
  const [savingDraft, setSavingDraft] = useState(false)

  const [versions, setVersions] = useState<SavedVersion[]>([])
  const [savingVersion, setSavingVersion] = useState(false)

  async function loadAll() {
    try {
      const [p, allG, allB, vers] = await Promise.all([
        projects.get(projectId),
        garments.list(),
        books.list(),
        patterns.listByProject(projectId),
      ])
      if (!p) {
        setError('Progetto non trovato')
        setLoaded(true)
        return
      }
      setProject(p)
      setBook(allB[0]?.content ?? null)
      const match = allG.find(g => g.content.name === p.garmentName) ?? null
      setGarment(match?.content ?? null)
      setAllGarments(allG.map(g => g.content))
      setVersions(vers as SavedVersion[])
      setLoaded(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento')
      setLoaded(true)
    }
  }

  useEffect(() => {
    void loadAll()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  const source = editing ? draft : project

  const numericMeasurements = useMemo(() => {
    if (!source) return null
    // In edit mode draft.measurements are still numbers (we keep them as Record<string, number>)
    return source.measurements
  }, [source])

  const preview: ComputeResult | null = useMemo(() => {
    if (!source || !garment || !numericMeasurements) return null
    return computeWithInheritance(
      garment,
      allGarments,
      numericMeasurements,
      ease,
    )
  }, [source, garment, allGarments, numericMeasurements, ease])

  const codeMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const x of book?.measurementsBase ?? []) m.set(x.code, x.italianName)
    return m
  }, [book])

  const availableSizes = useMemo(() => {
    if (!book || !garment) return [] as string[]
    if (garment.section === 'donna') return book.sizesWomen.sizes
    if (garment.section === 'uomo') return book.sizesMen.sizes
    return []
  }, [book, garment])

  function startEdit() {
    if (!project) return
    setDraft({ ...project, measurements: { ...project.measurements } })
    setEditing(true)
    setError(null)
    setInfo(null)
  }

  function cancelEdit() {
    setEditing(false)
    setDraft(null)
    setError(null)
  }

  function setDraftField<K extends keyof ProjectContent>(key: K, value: ProjectContent[K]) {
    setDraft(d => (d ? { ...d, [key]: value } : d))
  }

  function setDraftMeasure(code: string, raw: string) {
    const v = parseFloat(raw.replace(',', '.'))
    setDraft(d => {
      if (!d) return d
      const next = { ...d.measurements }
      if (Number.isFinite(v)) next[code] = v
      else delete next[code]
      return { ...d, measurements: next }
    })
  }

  function applySize(size: string) {
    if (!book || !garment) return
    const fromSize = measurementsFromSize(size, garment.section, book)
    setDraft(d => {
      if (!d) return d
      const next: Record<string, number> = { ...d.measurements }
      for (const code of garment.measurementsRequired) {
        const v = fromSize[code]
        if (typeof v === 'number') next[code] = v
      }
      return { ...d, sourceSizeLabel: size, measurements: next }
    })
  }

  async function saveDraft() {
    if (!draft || !project) return
    if (!draft.name.trim()) {
      setError('Il nome non può essere vuoto')
      return
    }
    setSavingDraft(true)
    setError(null)
    try {
      await projects.update(projectId, draft)
      setProject(draft)
      setEditing(false)
      setDraft(null)
      setInfo('Modifiche salvate')
      setTimeout(() => setInfo(null), 2500)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio')
    } finally {
      setSavingDraft(false)
    }
  }

  async function handleDelete() {
    if (!confirm('Eliminare questo progetto? L\'azione è irreversibile.')) return
    try {
      await projects.delete(projectId)
      onDeleted()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione')
    }
  }

  async function saveVersion() {
    if (!preview || !source) return
    setSavingVersion(true)
    setError(null)
    try {
      const content: PatternContent = {
        computedAt: new Date().toISOString(),
        pieces: preview.pieces.map(p => ({
          id: p.id,
          italianLabel: p.italianLabel,
          points: p.points.map(pt => ({ label: pt.label, x: pt.x, y: pt.y })),
          segments: p.segments,
        })),
        appliedMeasurements: { ...source.measurements, vest: ease },
        steps: garment?.steps ?? [],
        unit: 'cm',
      }
      await patterns.create(projectId, content)
      const vers = await patterns.listByProject(projectId)
      setVersions(vers as SavedVersion[])
      setInfo(`Versione salvata (#${vers.length})`)
      setTimeout(() => setInfo(null), 2500)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio versione')
    } finally {
      setSavingVersion(false)
    }
  }

  async function deleteVersion(id: string) {
    if (!confirm('Eliminare questa versione del cartamodello?')) return
    try {
      await patterns.delete(id)
      const vers = await patterns.listByProject(projectId)
      setVersions(vers as SavedVersion[])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione versione')
    }
  }

  if (!loaded) return <p className="muted">Caricamento…</p>

  if (!project) {
    return (
      <section className="tab-section">
        <button className="back-button" onClick={onBack}>← Indietro</button>
        <div className="empty-state">
          <p>{error ?? 'Progetto non trovato'}</p>
        </div>
      </section>
    )
  }

  const view = source ?? project

  return (
    <section className="tab-section detail-section">
      <button className="back-button" onClick={onBack}>← Progetti</button>

      <header className={`detail-header card ${garment ? `section-${garment.section}` : ''}`}>
        <div className="garment-card-badges">
          {garment && (
            <span className={`badge section-${garment.section}`}>
              {SECTION_LABELS[garment.section]}
            </span>
          )}
          <span className="badge muted">
            {view.developmentType === 'made-to-measure' ? 'su misura' : 'piano taglie'}
          </span>
          {view.sourceSizeLabel && (
            <span className="badge scale">tg. {view.sourceSizeLabel}</span>
          )}
        </div>
        {editing && draft ? (
          <input
            type="text"
            value={draft.name}
            onChange={e => setDraftField('name', e.target.value)}
            className="header-edit-input"
          />
        ) : (
          <h3>{view.name}</h3>
        )}
        <p className="header-meta">
          {view.garmentName}
          {' · '}
          {Object.keys(view.measurements).length} misure
        </p>
      </header>

      {!garment && (
        <div className="status status-err">
          Capo "{project.garmentName}" non più presente nel libro. Impossibile
          ricalcolare il cartamodello.
        </div>
      )}

      {info && <div className="status status-ok">{info}</div>}
      {error && <div className="status status-err">{error}</div>}

      <div className="row detail-actions" style={{ marginTop: '0.4rem' }}>
        {!editing ? (
          <button className="secondary" onClick={startEdit} disabled={!garment}>
            Modifica
          </button>
        ) : (
          <>
            <button onClick={saveDraft} disabled={savingDraft}>
              {savingDraft ? 'Salvataggio…' : 'Salva modifiche'}
            </button>
            <button className="secondary" onClick={cancelEdit} disabled={savingDraft}>
              Annulla
            </button>
          </>
        )}
      </div>

      <div className="detail-block">
        <h4>Misure</h4>
        {editing && draft && draft.developmentType === 'size-chart' && availableSizes.length > 0 && (
          <label style={{ marginBottom: '0.6rem', maxWidth: 280 }}>
            Taglia di riferimento
            <select
              value={draft.sourceSizeLabel ?? ''}
              onChange={e => applySize(e.target.value)}
            >
              <option value="">— scegli taglia —</option>
              {availableSizes.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        )}

        <div className="measurements-grid">
          {(garment?.measurementsRequired ?? Object.keys(view.measurements)).filter(c => !isDerivedCode(c)).map(code => (
            <div key={code} className="measurement-input">
              <span className="m-code">{code}</span>
              <span className="m-name">{codeMap.get(code) ?? '—'}</span>
              {editing && draft ? (
                <input
                  type="text"
                  inputMode="decimal"
                  value={
                    typeof draft.measurements[code] === 'number'
                      ? String(draft.measurements[code])
                      : ''
                  }
                  onChange={e => setDraftMeasure(code, e.target.value)}
                  placeholder="cm"
                />
              ) : (
                <span className="m-value">
                  {typeof view.measurements[code] === 'number'
                    ? `${view.measurements[code]} cm`
                    : '—'}
                </span>
              )}
            </div>
          ))}
        </div>

        <label style={{ marginTop: '1rem', maxWidth: 200 }}>
          Vestibilità (cm)
          <input
            type="number"
            step="0.5"
            value={ease}
            onChange={e => setEase(parseFloat(e.target.value) || 0)}
          />
        </label>
      </div>

      <div className="detail-block">
        <h4>Note</h4>
        {editing && draft ? (
          <textarea
            rows={4}
            value={draft.additionalNotes}
            onChange={e => setDraftField('additionalNotes', e.target.value)}
            placeholder="Note opzionali..."
            style={{ resize: 'vertical' }}
          />
        ) : view.additionalNotes ? (
          <p style={{ whiteSpace: 'pre-wrap' }}>{view.additionalNotes}</p>
        ) : (
          <p className="muted">Nessuna nota.</p>
        )}
      </div>

      {preview && (
        <div className="detail-block">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h4 style={{ margin: 0 }}>
              Cartamodello
              {preview.errors.length > 0 && (
                <span className="status status-err" style={{ marginLeft: '0.6rem', fontSize: '0.8rem' }}>
                  {preview.errors.length} errori
                </span>
              )}
            </h4>
            <div className="row" style={{ gap: '0.4rem' }}>
              <button
                className="secondary"
                onClick={() => printPiecesA0(preview.pieces, view.name)}
                disabled={preview.pieces.every(p => p.points.length === 0)}
                title="Stampa tutti i pezzi su fogli A0 ottimizzando lo spazio"
              >
                Stampa tutti (A0)
              </button>
              <button
                className="secondary"
                onClick={saveVersion}
                disabled={savingVersion || preview.errors.length > 0}
                title={preview.errors.length > 0 ? 'Risolvi gli errori prima di salvare' : 'Salva snapshot'}
              >
                {savingVersion ? 'Salvataggio…' : 'Salva versione'}
              </button>
            </div>
          </div>

          {preview.pieces.map(p => (
            <div key={p.id} className="piece-block card" style={{ marginTop: '0.8rem' }}>
              <div className="piece-block-header">
                <strong>{p.italianLabel}</strong>
                <span className="muted">
                  {p.points.length} punti · {p.segments.length} segmenti
                </span>
              </div>
              {p.points.length > 0 && <PiecePreview piece={p} />}
            </div>
          ))}

          {preview.errors.length > 0 && (
            <details open className="card" style={{ marginTop: '1rem', padding: '0.8rem 1rem' }}>
              <summary><strong>Errori di calcolo ({preview.errors.length})</strong></summary>
              <ul className="muted" style={{ marginTop: '0.6rem' }}>
                {preview.errors.map((e, i) => (
                  <li key={i}>
                    {e.pieceLabel} · punto {e.pointLabel} · {e.axis}: <code>{e.formula}</code> — {e.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {versions.length > 0 && (
        <div className="detail-block">
          <h4>Versioni salvate <span className="muted">({versions.length})</span></h4>
          <ul className="versions-list">
            {versions.slice().reverse().map(v => (
              <li key={v.id} className="version-row">
                <div>
                  <strong>v{v.version}</strong>
                  <span className="muted" style={{ marginLeft: '0.6rem' }}>
                    {new Date(v.createdAt).toLocaleString('it-IT')}
                  </span>
                  <span className="muted" style={{ marginLeft: '0.6rem' }}>
                    {v.content.pieces.length} pezzi
                  </span>
                </div>
                <button
                  className="ghost-btn"
                  onClick={() => deleteVersion(v.id)}
                  style={{ borderColor: 'var(--error)', color: 'var(--error)' }}
                >
                  Elimina
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {garment && garment.steps.length > 0 && (
        <div className="detail-block">
          <h4>Passi di costruzione</h4>
          <ol className="steps-list">
            {garment.steps.map(s => (
              <li key={s.stepNumber}>
                <p>{s.italianText}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="row detail-actions">
        <button className="danger" onClick={handleDelete}>Elimina progetto</button>
      </div>
    </section>
  )
}
